import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "patternSelect",
    "highWaterMarkContainer",
    "highWaterMarkInput",
    "includeHistoryInput",
    "submitButton",
    "progressContainer",
    "statusText",
    "errorContainer",
    "errorMessage",
    "successContainer",
    "successMessage",
    "exportsLink"
  ]

  static values = {
    tableDefinitionId: Number,
    createUrl: String,
    exportsUrl: String
  }

  connect() {
    this.updateHighWaterMarkVisibility()
  }

  patternChanged() {
    this.updateHighWaterMarkVisibility()
  }

  updateHighWaterMarkVisibility() {
    const pattern = this.patternSelectTarget.value
    if (pattern === "incremental") {
      this.highWaterMarkContainerTarget.classList.remove("hidden")
      this.highWaterMarkInputTarget.required = true
    } else {
      this.highWaterMarkContainerTarget.classList.add("hidden")
      this.highWaterMarkInputTarget.required = false
    }
  }

  async startExport(event) {
    event.preventDefault()

    const pattern = this.patternSelectTarget.value
    const highWaterMark = this.highWaterMarkInputTarget.value
    const includeHistory = this.includeHistoryInputTarget.checked
    // The datetime-local input carries no time zone, so it is read as a wall clock time in the
    // browser's zone and sent as an absolute instant. Sending the naive string instead would
    // have the server read it as UTC and silently shift the mark by the local offset.
    const highWaterMarkAt = this.toInstant(highWaterMark)

    // Validate
    if (!pattern) {
      this.showError("Please select an export pattern")
      return
    }

    if (pattern === "incremental" && !highWaterMark) {
      this.showError("High water mark is required for incremental exports")
      return
    }

    if (pattern === "incremental" && !highWaterMarkAt) {
      this.showError("Enter a valid high water mark date and time")
      return
    }

    // Reset UI
    this.hideError()
    this.hideSuccess()
    this.showProgress("Starting export...")
    this.submitButtonTarget.disabled = true

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.getCSRFToken()
        },
        body: JSON.stringify({
          pattern: pattern,
          high_water_mark: highWaterMarkAt,
          include_history: includeHistory
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || data.message || "Failed to start export")
      }

      // Success
      this.hideProgress()
      this.showSuccess(data.export_id, data.exports_url)

    } catch (error) {
      this.hideProgress()
      this.showError(error.message)
      this.submitButtonTarget.disabled = false
    }
  }

  // A local wall clock time from the datetime-local input as an ISO 8601 instant in UTC.
  toInstant(value) {
    if (!value) return null
    const local = new Date(value)
    return isNaN(local.getTime()) ? null : local.toISOString()
  }

  showProgress(message) {
    this.progressContainerTarget.classList.remove("hidden")
    this.statusTextTarget.textContent = message
  }

  hideProgress() {
    this.progressContainerTarget.classList.add("hidden")
  }

  showError(message) {
    this.errorContainerTarget.classList.remove("hidden")
    this.errorMessageTarget.textContent = message
  }

  hideError() {
    this.errorContainerTarget.classList.add("hidden")
  }

  showSuccess(exportId, exportsUrl) {
    this.successContainerTarget.classList.remove("hidden")
    this.successMessageTarget.textContent = `Export #${exportId} has been queued and is processing.`
    this.exportsLinkTarget.href = exportsUrl
    this.submitButtonTarget.disabled = true
  }

  hideSuccess() {
    this.successContainerTarget.classList.add("hidden")
  }

  getCSRFToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
